import React, { useEffect, useState } from 'react'
import { Box, Container, SimpleGrid, Text, TextInput, Stack, Badge, Loader, Center, Button, Group } from '@mantine/core'
import { useDebouncedValue } from '@mantine/hooks'
import agent from '../../app/api/agent'
import { CartridgeSpecDto, AmmoProductDto } from '../../app/models/ammo'
import { PagedResult } from '../../app/models/pagedResult'
import { useHistory } from 'react-router-dom'
import { useStore } from '../../app/stores/store'
import { observer } from 'mobx-react-lite'

const AMMO_TYPES = ['Pistol', 'Rifle', 'Shotgun', 'Rimfire']

const CartridgeSpecCard: React.FC<{
  spec: CartridgeSpecDto;
  isSelected: boolean;
  productCount?: number;
  onClick: () => void;
}> = ({ spec, isSelected, productCount, onClick }) => (
  <Box
    onClick={onClick}
    sx={(theme) => ({
      padding: '12px',
      borderRadius: '6px',
      border: isSelected ? '2px solid var(--gs-ammo-border)' : '1px solid var(--gs-border)',
      background: isSelected ? 'var(--gs-surface2)' : 'var(--gs-surface)',
      cursor: 'pointer',
      transition: 'all 0.2s',
      '&:hover': {
        borderColor: 'var(--gs-ammo-border)',
        background: 'var(--gs-surface2)'
      }
    })}
  >
    <Text weight={700} size="sm" sx={{ marginBottom: '4px' }}>
      {spec.name}
    </Text>
    <Text size="xs" sx={{ color: 'var(--gs-muted)', fontFamily: 'JetBrains Mono, monospace' }}>
      {spec.shortName} • {spec.caliber}
    </Text>
    {productCount !== undefined && (
      <Badge
        size="sm"
        sx={{ marginTop: '8px', background: 'var(--gs-gold)' }}
        color="yellow"
      >
        {productCount} loads
      </Badge>
    )}
  </Box>
)

const AmmoProductCard: React.FC<{
  product: AmmoProductDto;
  onClick: () => void;
}> = ({ product, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      padding: '12px',
      borderRadius: '6px',
      border: '1px solid var(--gs-border)',
      background: 'var(--gs-surface)',
      cursor: 'pointer',
      transition: 'all 0.2s',
      '&:hover': {
        borderColor: 'var(--gs-ammo-border)',
        background: 'var(--gs-surface2)'
      }
    }}
  >
    <Text weight={700} size="sm" sx={{ marginBottom: '4px' }}>
      {product.brand} {product.name}
    </Text>
    <Text size="xs" sx={{ color: 'var(--gs-text)', fontFamily: 'JetBrains Mono, monospace' }}>
      {product.grainWeight}gr {product.bulletType}
    </Text>
  </Box>
)

const AmmoDashboard = observer(() => {
  const history = useHistory()
  const { userStore } = useStore()
  const [searchText, setSearchText] = useState('')
  const [debouncedSearch] = useDebouncedValue(searchText, 300)
  const [typeFilter, setTypeFilter] = useState<string | null>(null)
  const [cartridgeSpecs, setCartridgeSpecs] = useState<CartridgeSpecDto[]>([])
  const [loading, setLoading] = useState(false)
  const [activeCartridge, setActiveCartridge] = useState<CartridgeSpecDto | null>(null)
  const [ammoProducts, setAmmoProducts] = useState<AmmoProductDto[]>([])
  const [ammoLoading, setAmmoLoading] = useState(false)

  // Load cartridge specs
  useEffect(() => {
    setLoading(true)
    agent.CartridgeSpecs.list({
      searchText: debouncedSearch,
      type: typeFilter || undefined,
      pageNumber: 1,
      pageSize: 50
    })
      .then((result: PagedResult<CartridgeSpecDto>) => {
        setCartridgeSpecs(result.items)
      })
      .finally(() => setLoading(false))
  }, [debouncedSearch, typeFilter])

  // Load ammo products for selected cartridge
  useEffect(() => {
    if (!activeCartridge) {
      setAmmoProducts([])
      return
    }

    setAmmoLoading(true)
    agent.AmmoProducts.list({
      cartridgeSpecId: activeCartridge.id,
      pageNumber: 1,
      pageSize: 50
    })
      .then((result: PagedResult<AmmoProductDto>) => {
        setAmmoProducts(result.items)
      })
      .finally(() => setAmmoLoading(false))
  }, [activeCartridge])

  const canSubmit = userStore.user && userStore.user.ammoScore >= 10

  return (
    <Box sx={{ background: 'var(--gs-bg)', minHeight: '100vh' }}>
      {/* Header Bar */}
      <Box
        sx={{
          background: 'var(--gs-ammo-border)',
          padding: '24px 0',
          borderBottom: '2px solid var(--gs-border)',
          marginBottom: '24px'
        }}
      >
        <Container size="xl">
          <Stack spacing={0}>
            <Text
              sx={{
                fontFamily: 'Oswald, sans-serif',
                fontSize: '32px',
                fontWeight: 700,
                color: 'white',
                marginBottom: '4px'
              }}
            >
              AMMO REGISTRY
            </Text>
            <Text sx={{ color: 'var(--gs-muted)', fontSize: '14px', marginBottom: '12px' }}>
              Cartridge specifications and manufacturer loads.
            </Text>
            <Group spacing="xs" mb="md">
              <Button
                variant="light"
                size="sm"
                onClick={() => {}}
                sx={{ cursor: 'default' }}
              >
                Specifications
              </Button>
              <Button
                variant="light"
                size="sm"
                onClick={() => history.push('/ammo/reloading')}
              >
                Reloader Forum
              </Button>
            </Group>
            {canSubmit && (
              <Button
                size="sm"
                sx={{ background: 'var(--gs-accent)', width: 'fit-content' }}
                onClick={() => history.push('/ammo/submit')}
              >
                + Submit Ammo
              </Button>
            )}
          </Stack>
        </Container>
      </Box>

      <Container size="xl">
        <SimpleGrid cols={2} spacing="md" breakpoints={[{ maxWidth: 'sm', cols: 1 }]}>
          {/* Left Panel - Cartridge Specs */}
          <Box>
            <Stack spacing="md">
              <div>
                <Text weight={700} size="lg" sx={{ marginBottom: '8px' }}>
                  Cartridge Specs
                </Text>
                <TextInput
                  placeholder="Search by name or caliber..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.currentTarget.value)}
                  sx={{ marginBottom: '12px' }}
                />
                <Group spacing="xs" sx={{ marginBottom: '12px' }}>
                  <Badge
                    onClick={() => setTypeFilter(null)}
                    sx={{ cursor: 'pointer' }}
                    variant={typeFilter === null ? 'filled' : 'outline'}
                  >
                    All
                  </Badge>
                  {AMMO_TYPES.map((type) => (
                    <Badge
                      key={type}
                      onClick={() => setTypeFilter(type)}
                      sx={{ cursor: 'pointer' }}
                      variant={typeFilter === type ? 'filled' : 'outline'}
                    >
                      {type}
                    </Badge>
                  ))}
                </Group>
              </div>

              {loading
                ? (
                <Center>
                  <Loader />
                </Center>
                  )
                : (
                <Stack spacing="sm">
                  {cartridgeSpecs.map((spec) => (
                    <CartridgeSpecCard
                      key={spec.id}
                      spec={spec}
                      isSelected={activeCartridge?.id === spec.id}
                      onClick={() => setActiveCartridge(spec)}
                    />
                  ))}
                </Stack>
                  )}
            </Stack>
          </Box>

          {/* Right Panel - Ammo Products */}
          <Box>
            {!activeCartridge
              ? (
              <Box
                sx={{
                  padding: '32px',
                  textAlign: 'center',
                  border: '1px dashed var(--gs-border)',
                  borderRadius: '6px'
                }}
              >
                <Text sx={{ color: 'var(--gs-muted)' }}>
                  Select a cartridge to browse loads.
                </Text>
              </Box>
                )
              : (
              <Stack spacing="md">
                <div>
                  <Text weight={700} size="lg" sx={{ marginBottom: '4px' }}>
                    {activeCartridge.name}
                  </Text>
                  <Text
                    size="xs"
                    sx={{
                      color: 'var(--gs-muted)',
                      fontFamily: 'JetBrains Mono, monospace'
                    }}
                  >
                    {activeCartridge.caliber} • {activeCartridge.type}
                  </Text>
                </div>

                {ammoLoading
                  ? (
                  <Center>
                    <Loader />
                  </Center>
                    )
                  : ammoProducts.length === 0
                    ? (
                  <Box
                    sx={{
                      padding: '16px',
                      textAlign: 'center',
                      border: '1px dashed var(--gs-border)',
                      borderRadius: '6px'
                    }}
                  >
                    <Text size="sm" sx={{ color: 'var(--gs-muted)' }}>
                      No loads for this cartridge yet.
                    </Text>
                  </Box>
                      )
                    : (
                  <SimpleGrid cols={2} spacing="xs" breakpoints={[{ maxWidth: 'sm', cols: 1 }]}>
                    {ammoProducts.map((product) => (
                      <AmmoProductCard
                        key={product.id}
                        product={product}
                        onClick={() => history.push(`/ammo/products/${product.id}`)}
                      />
                    ))}
                  </SimpleGrid>
                      )}
              </Stack>
                )}
          </Box>
        </SimpleGrid>
      </Container>
    </Box>
  )
})

export default AmmoDashboard
